/**
 * MaterialMovement.jsx — advanced any-to-any material transfer console.
 *
 * Supports 4 direction combinations: warehouse↔warehouse, warehouse↔site,
 * site↔site, site→warehouse (return). Full workflow:
 *   draft → in_transit → received / partial_receipt → verified
 * with cancel + reversal at any pre-verified stage.
 *
 * KPI tile bar · Direction filter chips · Filterable list · Rich transfer
 * sheet with source/destination pickers · Searchable material picker that
 * queries live source stock · Transport/e-way metadata · MTN PDF · Material
 * journey drawer.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, fmtDate, formatApiErrorDetail } from "@/lib/apiClient";
import { PageHeader, EmptyState } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Plus, Trash, CheckCircle, Truck, ArrowRight, MagnifyingGlass, Path, Warning,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { STATUS_PILL, DIR_META, dirKey, iconForKind } from "@/pages/warehouse/_movementShared";
import TransferSheet from "@/pages/warehouse/TransferSheet";

export default function MaterialMovement() {
  const [rows, setRows] = useState([]);
  const [kpi, setKpi] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState({ status: "", direction: "" });
  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [journeyOpen, setJourneyOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const q = new URLSearchParams();
      if (filter.status) q.set("status", filter.status);
      const [tr, w, p, k] = await Promise.all([
        api.get(`/material-transfers${q.toString() ? "?" + q.toString() : ""}`),
        api.get("/warehouses"),
        api.get("/projects"),
        api.get("/material-transfers/summary/kpi"),
      ]);
      setRows(tr.data || []);
      setWarehouses(w.data || []);
      setProjects(p.data || []);
      setKpi(k.data || null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }, [filter.status]);
  useEffect(() => { load(); }, [load]);

  const filteredRows = useMemo(() => {
    if (!filter.direction) return rows;
    return rows.filter((r) => dirKey(r) === filter.direction);
  }, [rows, filter.direction]);

  const doAction = async (tid, action, body = {}) => {
    try {
      const { data } = await api.post(`/material-transfers/${tid}/${action}`, body);
      toast.success(`Transfer ${data.transfer_no} → ${data.status.replace("_", " ")}`);
      setViewing(data);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (tid) => {
    if (!window.confirm("Delete this draft transfer?")) return;
    try {
      await api.delete(`/material-transfers/${tid}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="space-y-6" data-testid="material-movement-page">
      <PageHeader
        title="Material Movement"
        description="Any-to-any material transfers · Warehouse ↔ Site · Site ↔ Site · Returns · Full MTN workflow with dispatch, receive & verify"
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setJourneyOpen(true)} className="rounded-sm" data-testid="mm-journey-btn">
              <Path size={13} className="mr-1.5" /> Material Journey
            </Button>
            <Button onClick={() => setCreating(true)} className="rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white" data-testid="mm-new-btn">
              <Plus size={13} className="mr-1.5" /> New Transfer
            </Button>
          </div>
        }
      />

      {/* --- KPI strip --- */}
      {kpi && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="mm-kpi">
          <KpiTile testId="kpi-total" label="Total Transfers" value={kpi.total_transfers} accent="slate" />
          <KpiTile testId="kpi-transit" label="In Transit" value={kpi.in_transit} accent="amber" icon={<Truck size={14} />} />
          <KpiTile testId="kpi-partial" label="Partial Receipt" value={kpi.partial_receipt} accent="blue" icon={<Warning size={14} />} />
          <KpiTile testId="kpi-verified" label="Verified" value={kpi.verified} accent="purple" icon={<CheckCircle size={14} />} />
          <KpiTile testId="kpi-directions" label="Direction Mix" value={Object.keys(kpi.directions || {}).length} accent="orange" icon={<Path size={14} />} />
        </div>
      )}

      {/* --- Filter chips --- */}
      <div className="flex gap-2 flex-wrap text-[11px]" data-testid="mm-filters">
        <span className="font-semibold uppercase tracking-widest text-slate-500 py-1.5">Status:</span>
        {["", "draft", "in_transit", "partial_receipt", "received", "verified", "cancelled"].map((s) => (
          <button
            key={s || "all"}
            onClick={() => setFilter((f) => ({ ...f, status: s }))}
            className={`px-2.5 py-1 rounded-sm border ${filter.status === s ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white border-slate-200 hover:border-slate-400"}`}
            data-testid={`mm-filter-status-${s || "all"}`}
          >
            {s ? s.replace("_", " ") : "All"}
          </button>
        ))}
        <span className="font-semibold uppercase tracking-widest text-slate-500 py-1.5 ml-4">Direction:</span>
        {["", "warehouse_to_warehouse", "warehouse_to_site", "site_to_site", "site_to_warehouse"].map((d) => (
          <button
            key={d || "all-dir"}
            onClick={() => setFilter((f) => ({ ...f, direction: d }))}
            className={`px-2.5 py-1 rounded-sm border ${filter.direction === d ? "bg-[#EA580C] text-white border-[#EA580C]" : "bg-white border-slate-200 hover:border-slate-400"}`}
            data-testid={`mm-filter-dir-${d || "all"}`}
          >
            {d ? DIR_META[d].label : "All"}
          </button>
        ))}
      </div>

      {/* --- Transfers table --- */}
      {filteredRows.length === 0 ? (
        <EmptyState title="No transfers yet" description="Click 'New Transfer' to create your first movement. Supports Warehouse↔Site, Site↔Site and Return flows with full dispatch/receive/verify workflow." />
      ) : (
        <div className="border border-slate-200 rounded-sm bg-white overflow-hidden">
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead className="text-[10px] font-bold uppercase tracking-widest">MTN #</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-widest">Date</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-widest">Source</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-widest">→</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-widest">Destination</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-widest text-right">Lines</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-widest">Vehicle</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-widest">Status</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-widest text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRows.map((r) => (
                <TableRow key={r.id} data-testid={`mm-row-${r.id}`}>
                  <TableCell className="font-mono text-[12px] font-semibold">{r.transfer_no}</TableCell>
                  <TableCell className="text-[11.5px]">{fmtDate(r.transfer_date)}</TableCell>
                  <TableCell className="text-[11.5px]">{iconForKind(r.source?.kind)} {r.source?.name}</TableCell>
                  <TableCell><span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${DIR_META[dirKey(r)]?.cls || ""}`}>{DIR_META[dirKey(r)]?.label || ""}</span></TableCell>
                  <TableCell className="text-[11.5px]">{iconForKind(r.destination?.kind)} {r.destination?.name}</TableCell>
                  <TableCell className="text-[11.5px] text-right font-mono">{(r.lines || []).length}</TableCell>
                  <TableCell className="text-[11px] font-mono">{r.vehicle_no || "—"}</TableCell>
                  <TableCell><span className={`px-2 py-0.5 rounded-sm text-[10px] font-semibold border ${STATUS_PILL[r.status] || "bg-slate-100"}`} data-testid={`mm-status-${r.id}`}>{(r.status || "").replace("_", " ")}</span></TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" variant="outline" onClick={() => setViewing(r)} className="rounded-sm h-7 text-[11px]" data-testid={`mm-open-${r.id}`}>Open</Button>
                      {r.status === "draft" && <Button size="sm" variant="outline" onClick={() => remove(r.id)} className="rounded-sm h-7 text-[11px] text-red-600 border-red-200 hover:bg-red-50" data-testid={`mm-del-${r.id}`}><Trash size={11} /></Button>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* --- Create / View sheet --- */}
      <TransferSheet
        open={creating || !!viewing}
        onClose={() => { setCreating(false); setViewing(null); }}
        transfer={viewing}
        warehouses={warehouses}
        projects={projects}
        onSaved={() => { load(); setCreating(false); }}
        onAction={doAction}
      />

      {/* --- Material journey drawer --- */}
      <JourneyDialog open={journeyOpen} onClose={() => setJourneyOpen(false)} />
    </div>
  );
}

function KpiTile({ label, value, accent, icon, testId }) {
  const accentCls = {
    slate: "border-slate-200 text-slate-700",
    amber: "border-amber-200 text-amber-800 bg-amber-50",
    blue: "border-blue-200 text-blue-800 bg-blue-50",
    purple: "border-purple-200 text-purple-800 bg-purple-50",
    orange: "border-orange-200 text-orange-800 bg-orange-50",
  }[accent] || "border-slate-200";
  return (
    <div className={`border rounded-sm p-3 ${accentCls}`} data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest opacity-70 mb-1">{icon}{label}</div>
      <div className="text-2xl font-semibold font-mono">{value ?? 0}</div>
    </div>
  );
}

// =========================================================================
// TransferSheet — create/edit + workflow actions
// =========================================================================
function JourneyDialog({ open, onClose }) {
  const [q, setQ] = useState("");
  const [trail, setTrail] = useState(null);
  const [loading, setLoading] = useState(false);

  const search = async () => {
    if (!q.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/material-transfers/journey?name=${encodeURIComponent(q.trim())}`);
      setTrail(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="mm-journey-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Path size={16} /> Material Journey</DialogTitle>
          <DialogDescription>Trace any material's full history — every dispatch, receive, and reversal across all warehouses & sites.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Material name (e.g. VRV Outdoor 22HP)" onKeyDown={(e) => e.key === "Enter" && search()} data-testid="mm-journey-input" />
          <Button onClick={search} disabled={loading} className="rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white" data-testid="mm-journey-search"><MagnifyingGlass size={13} className="mr-1" /> {loading ? "Searching…" : "Search"}</Button>
        </div>
        {trail && (
          <div className="max-h-[60vh] overflow-y-auto mt-3" data-testid="mm-journey-results">
            {trail.count === 0 ? (
              <div className="text-center py-8 text-slate-400 text-[12px]">No transfers found for "{q}"</div>
            ) : (
              <div className="space-y-2">
                <div className="text-[10px] uppercase font-bold tracking-widest text-slate-500">{trail.count} movement(s)</div>
                {trail.trail.map((row, i) => (
                  <div key={`${row.transfer_no}-${i}`} className="border border-slate-200 rounded-sm p-2.5 flex items-center gap-3 text-[11.5px]">
                    <div className="font-mono font-bold text-[12px] w-16">{row.transfer_no}</div>
                    <div className="w-20 text-slate-500 font-mono text-[10px]">{fmtDate(row.date)}</div>
                    <div className="flex-1 flex items-center gap-1 truncate">
                      {iconForKind(row.source?.kind)}<span className="truncate">{row.source?.name}</span>
                      <ArrowRight size={11} className="text-slate-400 mx-1" />
                      {iconForKind(row.destination?.kind)}<span className="truncate">{row.destination?.name}</span>
                    </div>
                    <div className="font-mono text-[11px]">
                      {row.qty}{row.received_qty != null && row.received_qty !== row.qty && <span className="text-amber-700"> → {row.received_qty}</span>}
                    </div>
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold border ${STATUS_PILL[row.status] || ""}`}>{(row.status || "").replace("_", " ")}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
